import { useEffect, useState } from "react";

import {
  Button,
  Field,
  HStack,
  Input,
  Select,
  Switch,
  Text,
  Textarea,
  VStack,
} from "components/ui";
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
} from "components/ui/dialog";
import { useTranslation } from "hooks/useTranslation";
import {
  AiConnector,
  AiConnectorKindName,
  ApiDialectName,
  CatalogSyncModeName,
  ConnectorAuthSchemeName,
  aiGatewayService,
  apiDialectValue,
  apiDialects,
  authSchemeValue,
  authSchemes,
  catalogSyncModeValue,
  connectorKindValue,
  connectorKinds,
  readGatewayErrorCode,
} from "services/aiGatewayService";
import { getThemeColor } from "theme/colors";

interface ConnectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** `null` opens the form in create mode. */
  connector: AiConnector | null;
  /** Fires after a successful write so the page can reload every panel. */
  onSaved: () => void;
}

const DEFAULT_KIND: AiConnectorKindName = "SingleProvider";
const DEFAULT_DIALECT: ApiDialectName = "OpenAiResponses";
const DEFAULT_AUTH: ConnectorAuthSchemeName = "BearerToken";
const DEFAULT_SYNC: CatalogSyncModeName = "Curated";

/**
 * Create/edit form for a tenant AI connector.
 *
 * Three fields are conditional rather than always-on because they are meaningless outside their
 * case: `baseUrl` is required for a self-hosted endpoint and ignored for a first-party provider,
 * `region` only exists for Bedrock, and catalog sync only applies to an aggregator whose model
 * list lives upstream.
 *
 * The API key is write-only in both directions — the server never returns it, so on edit an empty
 * box means "leave the stored key alone" and is not the same as clearing it.
 */
const ConnectorModal = ({
  isOpen,
  onClose,
  connector,
  onSaved,
}: ConnectorModalProps) => {
  const { t } = useTranslation();
  const isEdit = connector !== null;

  const [name, setName] = useState("");
  const [provider, setProvider] = useState("");
  const [kind, setKind] = useState<AiConnectorKindName>(DEFAULT_KIND);
  const [apiDialect, setApiDialect] = useState<ApiDialectName>(DEFAULT_DIALECT);
  const [authScheme, setAuthScheme] =
    useState<ConnectorAuthSchemeName>(DEFAULT_AUTH);
  const [catalogSyncMode, setCatalogSyncMode] =
    useState<CatalogSyncModeName>(DEFAULT_SYNC);
  const [region, setRegion] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [defaultModelName, setDefaultModelName] = useState("");
  const [timeoutSeconds, setTimeoutSeconds] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setApiKey("");
    setName(connector?.name ?? "");
    setProvider(connector?.provider ?? "");
    setKind(connector?.kind ?? DEFAULT_KIND);
    setApiDialect(connector?.apiDialect ?? DEFAULT_DIALECT);
    setAuthScheme(connector?.authScheme ?? DEFAULT_AUTH);
    setCatalogSyncMode(connector?.catalogSyncMode ?? DEFAULT_SYNC);
    setRegion(connector?.region ?? "");
    setBaseUrl(connector?.baseUrl ?? "");
    setDefaultModelName(connector?.defaultModelName ?? "");
    setTimeoutSeconds(
      connector?.timeoutSeconds != null ? String(connector.timeoutSeconds) : "",
    );
    setDescription(connector?.description ?? "");
    setIsDefault(connector?.isDefault ?? false);
    setIsActive(connector?.isActive ?? true);
  }, [isOpen, connector]);

  const requiresBaseUrl = kind === "CustomEndpoint";
  const requiresRegion = apiDialect === "BedrockConverse";
  const supportsCatalogSync = kind === "Aggregator";

  const validate = () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = t("app.aiGateway.errors.nameRequired");
    if (!isEdit && !provider.trim())
      next.provider = t("app.aiGateway.errors.providerRequired");
    if (requiresBaseUrl && !baseUrl.trim())
      next.baseUrl = t("app.aiGateway.errors.baseUrlRequired");
    if (requiresRegion && !region.trim())
      next.region = t("app.aiGateway.errors.regionRequired");
    if (timeoutSeconds.trim() && Number(timeoutSeconds) <= 0)
      next.timeoutSeconds = t("app.aiGateway.errors.timeoutInvalid");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const shared = {
      name: name.trim(),
      kind: connectorKindValue[kind],
      apiDialect: apiDialectValue[apiDialect],
      authScheme: authSchemeValue[authScheme],
      region: requiresRegion ? region.trim() : null,
      catalogSyncMode:
        catalogSyncModeValue[supportsCatalogSync ? catalogSyncMode : "Curated"],
      defaultModelName: defaultModelName.trim() || null,
      timeoutSeconds: timeoutSeconds.trim() ? Number(timeoutSeconds) : null,
      baseUrl: baseUrl.trim() || null,
      description: description.trim() || null,
      isDefault,
      isActive,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await aiGatewayService.updateConnector(connector!.id, {
          ...shared,
          // Empty means "keep the stored key"; clearing it is an explicit action elsewhere.
          apiKey: apiKey ? apiKey : null,
        });
      } else {
        await aiGatewayService.createConnector({
          ...shared,
          provider: provider.trim(),
          apiKey: apiKey || null,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      // The interceptor already toasted this. Re-render the one code that belongs on a field,
      // so the user can see which input the server rejected.
      if (readGatewayErrorCode(err) === "connectorUrlNotPublic") {
        setErrors({ baseUrl: t("app.aiGateway.errors.connectorUrlNotPublic") });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e: { open: boolean }) => !e.open && onClose()}
      size="lg"
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEdit
                ? t("app.aiGateway.modal.editTitle")
                : t("app.aiGateway.modal.createTitle")}
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <VStack align="stretch" gap={4}>
              <Field
                label={t("app.aiGateway.fields.name")}
                required
                invalid={!!errors.name}
                errorText={errors.name}
              >
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("app.aiGateway.fields.namePlaceholder")}
                />
              </Field>

              <Field
                label={t("app.aiGateway.fields.provider")}
                required={!isEdit}
                invalid={!!errors.provider}
                errorText={errors.provider}
                helperText={
                  isEdit
                    ? t("app.aiGateway.fields.providerLocked")
                    : t("app.aiGateway.fields.providerHelp")
                }
              >
                <Input
                  value={provider}
                  disabled={isEdit}
                  onChange={(e) => setProvider(e.target.value)}
                  placeholder={t("app.aiGateway.fields.providerPlaceholder")}
                />
              </Field>

              <HStack gap={4} align="flex-start" wrap="wrap">
                <Select
                  label={t("app.aiGateway.fields.kind")}
                  value={[kind]}
                  onValueChange={(d: { value: string[] }) =>
                    setKind(d.value[0] as AiConnectorKindName)
                  }
                  items={connectorKinds.map((k) => ({
                    label: t(`app.aiGateway.kinds.${k}`),
                    value: k,
                  }))}
                  helperText={t(`app.aiGateway.kindHelp.${kind}`)}
                />

                <Select
                  label={t("app.aiGateway.fields.apiDialect")}
                  value={[apiDialect]}
                  onValueChange={(d: { value: string[] }) =>
                    setApiDialect(d.value[0] as ApiDialectName)
                  }
                  items={apiDialects.map((d) => ({
                    label: t(`app.aiGateway.dialects.${d}`),
                    value: d,
                  }))}
                  helperText={t("app.aiGateway.fields.apiDialectHelp")}
                />
              </HStack>

              <HStack gap={4} align="flex-start" wrap="wrap">
                <Select
                  label={t("app.aiGateway.fields.authScheme")}
                  value={[authScheme]}
                  onValueChange={(d: { value: string[] }) =>
                    setAuthScheme(d.value[0] as ConnectorAuthSchemeName)
                  }
                  items={authSchemes.map((a) => ({
                    label: t(`app.aiGateway.authSchemes.${a}`),
                    value: a,
                  }))}
                />

                {supportsCatalogSync && (
                  <Select
                    label={t("app.aiGateway.fields.catalogSyncMode")}
                    value={[catalogSyncMode]}
                    onValueChange={(d: { value: string[] }) =>
                      setCatalogSyncMode(d.value[0] as CatalogSyncModeName)
                    }
                    items={(
                      ["Curated", "Discover"] as CatalogSyncModeName[]
                    ).map((m) => ({
                      label: t(`app.aiGateway.catalogSync.${m}`),
                      value: m,
                    }))}
                    helperText={t("app.aiGateway.fields.catalogSyncHelp")}
                  />
                )}
              </HStack>

              {requiresRegion && (
                <Field
                  label={t("app.aiGateway.fields.region")}
                  required
                  invalid={!!errors.region}
                  errorText={errors.region}
                  helperText={t("app.aiGateway.fields.regionHelp")}
                >
                  <Input
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder={t("app.aiGateway.fields.regionPlaceholder")}
                  />
                </Field>
              )}

              <Field
                label={t("app.aiGateway.fields.baseUrl")}
                required={requiresBaseUrl}
                invalid={!!errors.baseUrl}
                errorText={errors.baseUrl}
                helperText={t("app.aiGateway.fields.baseUrlHelp")}
              >
                <Input
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder={t("app.aiGateway.fields.baseUrlPlaceholder")}
                />
              </Field>

              <Field
                label={t("app.aiGateway.fields.apiKey")}
                helperText={
                  isEdit && connector?.hasApiKey
                    ? t("app.aiGateway.fields.apiKeyStored")
                    : t("app.aiGateway.fields.apiKeyHelp")
                }
              >
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={
                    isEdit && connector?.hasApiKey
                      ? t("app.aiGateway.fields.apiKeyKeepPlaceholder")
                      : t("app.aiGateway.fields.apiKeyPlaceholder")
                  }
                />
              </Field>

              <HStack gap={4} align="flex-start" wrap="wrap">
                <Field
                  label={t("app.aiGateway.fields.defaultModelName")}
                  helperText={t("app.aiGateway.fields.defaultModelNameHelp")}
                >
                  <Input
                    value={defaultModelName}
                    onChange={(e) => setDefaultModelName(e.target.value)}
                    placeholder={t(
                      "app.aiGateway.fields.defaultModelNamePlaceholder",
                    )}
                  />
                </Field>

                <Field
                  label={t("app.aiGateway.fields.timeoutSeconds")}
                  invalid={!!errors.timeoutSeconds}
                  errorText={errors.timeoutSeconds}
                  helperText={t("app.aiGateway.fields.timeoutSecondsHelp")}
                >
                  <Input
                    type="number"
                    min={1}
                    value={timeoutSeconds}
                    onChange={(e) => setTimeoutSeconds(e.target.value)}
                  />
                </Field>
              </HStack>

              <Field label={t("app.aiGateway.fields.description")}>
                <Textarea
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t("app.aiGateway.fields.descriptionPlaceholder")}
                />
              </Field>

              <VStack align="stretch" gap={3}>
                <Switch
                  checked={isDefault}
                  onCheckedChange={(e: { checked: boolean }) =>
                    setIsDefault(e.checked)
                  }
                >
                  <VStack align="start" gap={0}>
                    <Text fontSize="sm">
                      {t("app.aiGateway.fields.isDefault")}
                    </Text>
                    <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                      {t("app.aiGateway.fields.isDefaultHelp")}
                    </Text>
                  </VStack>
                </Switch>

                <Switch
                  checked={isActive}
                  onCheckedChange={(e: { checked: boolean }) =>
                    setIsActive(e.checked)
                  }
                >
                  <Text fontSize="sm">
                    {t("app.aiGateway.fields.isActive")}
                  </Text>
                </Switch>
              </VStack>
            </VStack>
          </DialogBody>

          <DialogFooter>
            <HStack gap={2} justify="flex-end">
              <DialogActionTrigger asChild>
                <Button intent="secondary" size="sm" onClick={onClose}>
                  {t("app.common.cancel")}
                </Button>
              </DialogActionTrigger>
              <Button
                intent="primary"
                size="sm"
                loading={saving}
                onClick={handleSave}
              >
                {t("app.common.save")}
              </Button>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  );
};

export default ConnectorModal;
